import { CircleAlert, X } from 'lucide-react'
import {
  useCallback,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type KeyboardEvent,
  type RefObject,
} from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'

/** Splits a pasted list on whitespace, commas and semicolons. */
const SPLIT_PATTERN = /[\s,;]+/

export interface EmailChipInputProps {
  /** The input's id, so the caller's `<label for>` reaches it. */
  id: string
  /** The committed addresses, in the order they were added. */
  emails: readonly string[]
  onEmailsChanged: (next: string[]) => void
  /** The committed addresses that are not valid (derived by the caller, case-insensitively matched). */
  invalid?: readonly string[]
  /** Ids of the hint and error elements the input is described by. */
  describedBy?: string
  placeholder?: string
  disabled?: boolean
  /** Test id for the input itself. */
  inputTestId?: string
  /** Lets the host move focus here (a dialog's initial focus). */
  inputRef?: RefObject<HTMLInputElement | null>
}

const sameAddress = (a: string, b: string): boolean =>
  a.toLowerCase() === b.toLowerCase()

/**
 * A multi-address chip input. Enter, comma, semicolon, Tab and blur commit the
 * typed text as a chip, valid or not — an invalid one renders as a red chip
 * with text and an icon, never silently dropped. A pasted list is split and
 * de-duplicated ignoring case. Backspace on an empty input removes the last
 * chip; clicking an invalid chip moves its text back into the input. Tab is
 * never swallowed, and adds/removes are announced in a polite live region.
 * Below `sm` every chip is its own 44px row.
 */
export function EmailChipInput({
  id,
  emails,
  onEmailsChanged,
  invalid = [],
  describedBy,
  placeholder,
  disabled = false,
  inputTestId,
  inputRef: hostInputRef,
}: EmailChipInputProps) {
  const { t } = useTranslation('projectWorkspace')
  const listId = useId()
  const ownInputRef = useRef<HTMLInputElement>(null)
  const inputRef = hostInputRef ?? ownInputRef
  const [draft, setDraft] = useState('')
  const [announcement, setAnnouncement] = useState('')

  const isInvalid = (email: string): boolean =>
    invalid.some((bad) => sameAddress(bad, email))
  const hasInvalid = emails.some(isInvalid)

  const commit = useCallback(
    (pieces: string[]): void => {
      const fresh: string[] = []
      for (const piece of pieces) {
        const value = piece.trim()
        if (value === '') continue
        const known = (existing: string): boolean =>
          sameAddress(existing, value)
        if (emails.some(known) || fresh.some(known)) continue
        fresh.push(value)
      }
      if (fresh.length === 0) return
      const next = [...emails, ...fresh]
      onEmailsChanged(next)
      const last = fresh[fresh.length - 1] ?? ''
      setAnnouncement(
        t('members.invite.announceAdded', { count: next.length, email: last })
      )
    },
    [emails, onEmailsChanged, t]
  )

  const onDraftChanged = (event: ChangeEvent<HTMLInputElement>): void => {
    setDraft(event.target.value)
  }

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter' || event.key === ',' || event.key === ';') {
      // Enter must never submit the dialog before the addresses are committed.
      event.preventDefault()
      commit(draft.split(SPLIT_PATTERN))
      setDraft('')
      return
    }
    if (event.key === 'Backspace' && draft === '' && emails.length > 0) {
      const removed = emails[emails.length - 1] ?? ''
      onEmailsChanged(emails.slice(0, -1))
      setAnnouncement(t('members.invite.announceRemoved', { email: removed }))
    }
  }

  const onInputBlurred = (): void => {
    if (draft.trim() === '') return
    commit(draft.split(SPLIT_PATTERN))
    setDraft('')
  }

  const onPasted = (event: ClipboardEvent<HTMLInputElement>): void => {
    event.preventDefault()
    commit(event.clipboardData.getData('text').split(SPLIT_PATTERN))
  }

  const onRemoveClicked = (email: string): void => {
    onEmailsChanged(emails.filter((existing) => existing !== email))
    setAnnouncement(t('members.invite.announceRemoved', { email }))
    inputRef.current?.focus()
  }

  const onInvalidChipClicked = (email: string): void => {
    onEmailsChanged(emails.filter((existing) => existing !== email))
    setDraft(email)
    inputRef.current?.focus()
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-1.5 rounded-md border border-input bg-card px-2 py-1.5 focus-within:ring-2 focus-within:ring-mint-ink sm:min-h-9 sm:flex-row sm:flex-wrap sm:items-center',
        hasInvalid && 'border-blocked',
        disabled && 'opacity-60'
      )}
    >
      {emails.length > 0 && (
        <ul
          id={listId}
          aria-label={t('members.invite.addedList')}
          className="contents"
        >
          {emails.map((email) => {
            const chipInvalid = isInvalid(email)
            return (
              <li
                key={email.toLowerCase()}
                data-testid="project-invite-chip"
                data-invalid={chipInvalid || undefined}
                className={cn(
                  'flex min-w-0 items-center gap-1 rounded-full border px-2 py-0.5 text-sm max-sm:min-h-[44px] max-sm:rounded-md',
                  chipInvalid
                    ? 'border-blocked bg-blocked-bg text-blocked'
                    : 'border-border bg-muted text-foreground'
                )}
              >
                {chipInvalid ? (
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => onInvalidChipClicked(email)}
                    className="flex min-w-0 items-center gap-1 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mint-ink max-sm:min-h-[44px] max-sm:flex-1"
                  >
                    <CircleAlert aria-hidden className="size-3.5 shrink-0" />
                    <span className="min-w-0 break-all">{email}</span>
                    <span className="sr-only">
                      {t('members.invite.invalidChip')}
                    </span>
                  </button>
                ) : (
                  <span className="min-w-0 break-all max-sm:flex-1">
                    {email}
                  </span>
                )}
                <button
                  type="button"
                  disabled={disabled}
                  aria-label={t('members.invite.remove', { email })}
                  onClick={() => onRemoveClicked(email)}
                  className="flex size-6 shrink-0 items-center justify-center rounded-full hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mint-ink max-sm:size-[44px]"
                >
                  <X aria-hidden className="size-3.5" />
                </button>
              </li>
            )
          })}
        </ul>
      )}
      <input
        ref={inputRef}
        id={id}
        type="text"
        inputMode="email"
        autoComplete="off"
        value={draft}
        disabled={disabled}
        placeholder={emails.length === 0 ? placeholder : undefined}
        aria-describedby={describedBy}
        aria-invalid={hasInvalid || undefined}
        data-testid={inputTestId}
        onChange={onDraftChanged}
        onKeyDown={onInputKeyDown}
        onBlur={onInputBlurred}
        onPaste={onPasted}
        className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground max-sm:min-h-[44px] sm:min-w-40"
      />
      <span className="sr-only" aria-live="polite">
        {announcement}
      </span>
    </div>
  )
}
